import React, { useEffect, useState } from "react";
import { currentUrl, unsavedChanges } from "../../App";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Container, InputGroup, Modal, Row, Spinner, Stack, Table } from "react-bootstrap";
import {
  fetchBeds,
  fetchFacilities,
  fetchRooms,
  patchRoom,
  postNote,
  postBedInspection,
  fetchLastBedInspection,
} from "../../services/httpClient";
import { isIndividuallyAssignableRoom, sortRoomsForFilter } from "../../helpers/housingAssignment";
import RoomFilterSelect from "./RoomFilterSelect";
import PageTitle from "../ui/PageTitle";
import BedInspectionCard from "./BedInspectionCard";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import InfractionView from "../my-cases/my-case/notes/InfractionView";
import { createRoot } from "react-dom/client";
import FormCheck from "../ui/FormCheckbox";
import useApp from "../../utils/useApp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileText, faDownload } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import IconEmbeddedButton from "../ui/IconEmbeddedButton";
import { InfoCircleOutline } from "../ui/InfoCircleOutline";

interface BedInspectionState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
  errMsg: any;
  setErrMsg: any;
}

const BedInspection = ({ setOrgToSave, setShowToast, setToastText, setToastBody, getData, errMsg, setErrMsg }: BedInspectionState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { showMenu }: any = useApp();
  const [filterRoom, setFilterRoom] = useState<any>();
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [infractions, setInfractions] = useState<any[]>([]);
  const [viewOccupied, setViewOccupied] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [canvas, setCanvas] = useState<any>();
  const [reportRunning, setReportRunning] = useState<boolean>(false);
  const [download, setDownload] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  // rooms whose inspection failed to save on the last "All Rooms" submit; a resubmit only retries these
  const [failedRoomIds, setFailedRoomIds] = useState<any[]>([]);
  const [showInspectionReport, setShowInspectionReport] = useState<boolean>(false);
  const [lastInspections, setLastInspections] = useState<any[]>([]);
  const [filteredInspections, setFilteredInspections] = useState<any[]>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState("");

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      setInfractions([]);
      setFailedRoomIds([]);
      setViewOccupied(false);
      setViewAll(true);
      getBeds(false);
    }
  }, [getData]);

  const getBeds = (maintainFilter: boolean) => {
    fetchBeds()
      .then((res: any) => {
        const bedList = res[0].data;

        bedList.sort((a: any, b: any) => {
          const roomCompare = a.room_key.localeCompare(b.room_key, undefined, {
            sensitivity: "base",
          });
          if (roomCompare !== 0) return roomCompare;

          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        });
        getRooms(bedList, maintainFilter);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getRooms = (allBeds: any, maintainFilter: boolean) => {
    Promise.all([fetchRooms(), fetchFacilities()])
      .then(([roomRes, facilityRes]: any) => {
        // only rooms whose beds are assigned one at a time are checked here, not dwellings or family rooms
        // and only if they have beds to check
        const roomIdsWithBeds = new Set(allBeds.map((bed: any) => bed.room_id));
        let roomList = roomRes[0].data.filter(
          (room: any) => roomIdsWithBeds.has(room.id) && isIndividuallyAssignableRoom(room, facilityRes[0].data),
        );
        const assignableRoomIds = new Set(roomList.map((room: any) => room.id));
        const bedList = allBeds.filter((bed: any) => assignableRoomIds.has(bed.room_id));
        setBeds(bedList);
        roomList = roomList.map((room: any) => {
          const bedCount = bedList.filter((bed: any) => bed.room_id === room.id).length;
          return {
            ...room,
            name: `${room.name} (${bedCount} beds)`,
            facility_name: facilityRes[0].data.find((f: any) => f.id === room.property_id)?.name ?? null,
          };
        });

        roomList = sortRoomsForFilter(roomList);

        roomList = roomList.map((room: any) => {
          if (room?.inspection_at) {
            const utcDate = new Date(room?.inspection_at + " UTC");
            const localTime = utcDate.toLocaleString(undefined, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            });
            return { ...room, inspection_at: localTime };
          } else {
            return room;
          }
        });

        setRooms(roomList);

        if (maintainFilter) {
          if (filterRoom === "0") {
            setFilteredBeds(bedList);
          } else {
            const room = roomList.find((room: any) => room.key === filterRoom?.key);
            setFilterRoom(room);
            const filtered = bedList.filter((bed: any) => bed.room_id === filterRoom?.id);
            setFilteredBeds(filtered);
          }
        } else if (roomList.length === 1) {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        } else {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0]?.id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        }
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterInspection = async (roomId: any) => {
    if (roomId === "0" || !roomId) {
      setFilteredInspections(lastInspections);
      return;
    }
    setFilteredInspections(lastInspections.filter((inspection: any) => inspection.room_id === roomId));
  };

  const filterByRoom = (e: any) => {
    setErrMsg("");
    setFailedRoomIds([]);
    const room = rooms.find((room: any) => room.key === e?.key);
    setFilterRoom(room);
    setInfractions([]);
    setViewOccupied(false);
    setViewAll(true);
    if (e?.key === "0") {
      setFilterRoom("0");
      setFilteredBeds(beds);
    } else {
      const roomId = rooms.find((room: any) => room.key === e?.key)?.id;
      const filtered = beds.filter((bed: any) => bed.room_id === roomId);
      setFilteredBeds(filtered);
    }
  };

  const filterByRoomReport = async (e: any) => {
    setErrMsg("");
    if (e?.key === "0") {
      setFilteredInspections(lastInspections);
      setFilterRoom("0");
      return;
    }
    const room = rooms.find((room: any) => room.key === e?.key);
    setFilterRoom(room);
    setInfractions([]);
    setViewOccupied(false);
    setViewAll(true);

    const roomId = rooms.find((room: any) => room.key === e?.key)?.id;
    const filtered = beds.filter((bed: any) => bed.room_id === roomId);
    setFilteredBeds(filtered);
    filterInspection(room?.id);
  };

  const onClickSubmit = () => {
    setIsUpdating(true);
    setErrMsg("");
    setSubmitted(true);
    let failedNotes: any[] = [];

    const updatedRoom = {
      inspection: true,
    };

    //if viewing all rooms
    if (filterRoom === "0") {
      // Assign room_id to each infraction based on bed match
      infractions.forEach((i: any) => {
        i.room_id = beds.find((bed: any) => bed.id === i.id)?.room_id;
      });

      // after a partial failure only the rooms that failed are submitted again
      const roomsToSubmit = failedRoomIds.length > 0 ? rooms.filter((room: any) => failedRoomIds.includes(room.id)) : rooms;
      const failedRooms: any[] = [];
      const savedRoomIds: any[] = [];

      Promise.all(
        roomsToSubmit.map((room: any) =>
          patchRoom(updatedRoom, room.id)
            .then((res: any) => {
              const inspectionId = res[0]?.data?.inspection_id;
              savedRoomIds.push(room.id);

              // Assign inspection ID to matching infractions
              infractions.forEach((i: any) => {
                if (i.room_id === room.id) {
                  i.inspection_id = inspectionId;
                  i.subject = {
                    ...i.subject,
                    name: room.name + ": " + i.subject.name || "",
                  };
                }
              });
            })
            .catch((err: any) => {
              console.log(`Failed to patch room ${room.name}:`, err?.response?.data?.msg);
              failedRooms.push(room);
            }),
        ),
      )
        .then(async () => {
          // infractions in a room that did not save have no inspection to belong to, so they are held back
          const submittable = infractions.filter((i: any) => savedRoomIds.includes(i.room_id));
          const heldBack = infractions.filter((i: any) => !savedRoomIds.includes(i.room_id));

          await Promise.allSettled(
            submittable.map((i: any) =>
              postNote(i.note, i.case_id)
                .then((res: any) => {
                  const noteId = res[0]?.data?.note_id;
                  i.note_id = noteId;
                })
                .catch((err: any) => {
                  failedNotes.push(i);
                  console.log(err?.response?.data?.msg);
                }),
            ),
          );
          await Promise.allSettled(
            submittable.map((i: any) =>
              postBedInspection(i).catch((err: any) => {
                console.log(err);
              }),
            ),
          );

          if (savedRoomIds.length > 0 && (submittable.length !== failedNotes.length || submittable.length === 0)) {
            setShowToast(true);
            setToastText("Bed Inspection Submitted");
            setToastBody(submittable.length - failedNotes.length + " Infraction(s) Created");
          }
          if (submittable.length > 0) {
            if (download) {
              downloadReport(submittable);
            }
          }

          if (failedRooms.length > 0) {
            // keep what the worker entered for the rooms that failed so they can submit again
            setFailedRoomIds(failedRooms.map((room: any) => room.id));
            setInfractions(heldBack);
            unsavedChanges.value = true;
            setOrgToSave(true);
          } else {
            setFailedRoomIds([]);
            setInfractions([]);
            unsavedChanges.value = false;
            setOrgToSave(false);
            setViewOccupied(false);
            setViewAll(true);
          }

          setIsLoading(true);
          getBeds(true);
          setIsUpdating(false);
          setSubmitted(false);

          // Error messaging
          const messages: string[] = [];
          if (failedRooms.length > 0) {
            messages.push("Failed to submit inspection for: \n" + failedRooms.map((room: any) => room.name).join("\n"));
          }
          if (failedNotes.length === 1) {
            messages.push("Failed to create infraction for bed: " + failedNotes.map((note) => note.bed_name));
          } else if (failedNotes.length > 1) {
            messages.push("Failed to create infractions for beds: \n" + failedNotes.map((note) => note.bed_name).join("\n"));
          }
          if (messages.length > 0) {
            setErrMsg(messages.join("\n"));
          }
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsUpdating(false);
          setSubmitted(false);
        });
      return;
    }

    patchRoom(updatedRoom, filterRoom?.id)
      .then(async (res: any) => {
        let inspectionId = res[0]?.data?.inspection_id;
        await Promise.allSettled(
          infractions.map((i: any, index: number) =>
            postNote(i.note, i?.case_id)
              .then((res: any) => {
                let noteId = res[0]?.data?.note_id;
                i.note_id = noteId;
                i.inspection_id = inspectionId;
                i.room_id = filterRoom?.id;
                i.subject = {
                  ...i.subject,
                  name: filterRoom.name + ": " + i.subject.name || "",
                };
                if (infractions.length === index + 1) {
                  if (download) {
                    downloadReport(infractions);
                  }
                }
              })
              .catch((err: any) => {
                failedNotes.push(i);
                console.log(err?.response?.data?.msg);
              }),
          ),
        );
        await Promise.allSettled(
          infractions.map((i: any, index: number) =>
            postBedInspection(i).catch((err: any) => {
              console.log(err);
            }),
          ),
        );

        unsavedChanges.value = false;
        setOrgToSave(false);
        // Don't show toast if no infractions successfully created
        if (infractions.length !== failedNotes.length || infractions.length === 0) {
          setShowToast(true);
          setToastText("Bed Inspection Submitted");
          setToastBody(infractions.length - failedNotes.length + " Infraction(s) Created");
        }

        setIsLoading(true);
        setInfractions([]);
        setViewOccupied(false);
        setViewAll(true);
        getBeds(true);
        setIsUpdating(false);
        setSubmitted(false);

        // Return the list of failed bed names as an error
        if (failedNotes.length === 1) {
          const failedNotesText = failedNotes.map((note) => note.bed_name);
          setErrMsg("Failed to create infraction for bed: " + failedNotesText);
        } else if (failedNotes.length > 0) {
          const failedNotesText = failedNotes.map((note) => note.bed_name).join("\n");
          setErrMsg("Failed to create infractions for beds: \n" + failedNotesText);
          return;
        }
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg + ": " + filterRoom?.name);
        setIsUpdating(false);
        setSubmitted(false);
      });
  };

  const onClickCancel = () => {
    setFailedRoomIds([]);
    unsavedChanges.value = false;
    setOrgToSave(false);
    setInfractions([]);
    setViewOccupied(false);
    setViewAll(true);
    getBeds(true);
  };

  const onClickViewOccupied = () => {
    setViewOccupied(true);
    setViewAll(false);
    if (filterRoom === "0") {
      const occupiedBeds = beds.filter((b: any) => b.occupant_id);
      setFilteredBeds(occupiedBeds);
    } else {
      const filtered = beds.filter((bed: any) => bed.room_id === filterRoom?.id);
      const occupiedBeds = filtered.filter((b: any) => b.occupant_id);
      setFilteredBeds(occupiedBeds);
    }
  };

  const onClickViewAll = () => {
    setViewOccupied(false);
    setViewAll(true);
    if (filterRoom === "0") {
      setFilteredBeds(beds);
    } else {
      const filtered = beds.filter((bed: any) => bed.room_id === filterRoom?.id);
      setFilteredBeds(filtered);
    }
  };

  useEffect(() => {
    if (reportRunning) {
      buildPDF("Infraction");
    }
    setReportRunning(false);
  }, [canvas]);

  function buildPDF(type: string) {
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const margin = 10;
      const headerHeight = 20; // Adjust as needed
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pdfWidth = pdf.internal.pageSize.getWidth() - 2 * margin;

      canvas.forEach((c: any, index: number) => {
        const imgData = c.toDataURL("image/png");
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
        let yPosition = margin + headerHeight;

        if (index > 0) {
          pdf.addPage();
        }

        // Add header to the first page
        pdf.setFontSize(12);
        pdf.text(type + " Report", margin, margin + 7);

        // Helper function to crop canvas
        const cropCanvas = (sourceCanvas: any, cropX: number, cropY: number, cropWidth: number, cropHeight: number) => {
          const tempCanvas = document.createElement("canvas");
          tempCanvas.width = cropWidth;
          tempCanvas.height = cropHeight;
          const ctx = tempCanvas.getContext("2d");
          ctx?.drawImage(sourceCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
          return tempCanvas;
        };

        // Check if the content fits on one page
        if (imgHeight <= pageHeight - yPosition) {
          pdf.addImage(imgData, "PNG", margin, yPosition, pdfWidth, imgHeight);
        } else {
          // Handle multi-page case
          let remainingHeight = c.height;
          let position = 0;
          const pageCanvasHeight = 1800;

          while (remainingHeight > 0) {
            const pageCanvas = cropCanvas(c, 0, position, c.width, pageCanvasHeight);
            const pageImgData = pageCanvas.toDataURL("image/png");
            pdf.addImage(pageImgData, "PNG", margin, yPosition, pdfWidth, pageCanvasHeight * (pdfWidth / c.width));

            remainingHeight -= pageCanvasHeight;
            position += pageCanvasHeight;

            if (remainingHeight > 0) {
              pdf.addPage();
              yPosition = margin + headerHeight;
              pdf.text(type + " Report", margin + 45, margin + 7);
            }
          }
        }
      });

      const formattedDate = new Date()
        .toISOString()
        .replace(/[-:.TZ]/g, "")
        .slice(0, 14);

      pdf.save(type + "_" + formattedDate + ".pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  }

  const downloadReport = async (infractions: any) => {
    setReportRunning(true);
    const canvases: HTMLCanvasElement[] = [];

    for (let i = 0; i < infractions.length; i++) {
      const infraction = infractions[i];

      //hidden container
      const hiddenContainer = document.createElement("div");
      hiddenContainer.style.position = "absolute";
      hiddenContainer.style.left = "-9999px"; // Off-screen
      hiddenContainer.style.top = "0";
      hiddenContainer.style.width = "800px";
      document.body.appendChild(hiddenContainer);

      // Render the HiddenReport component inside this container
      const root = createRoot(hiddenContainer);
      root.render(
        <Row className="mx-0">
          <InfractionView n={infraction} buildPDF={buildPDF} canvas={canvas} setCanvas={setCanvas} externalReportRunning={true} />
        </Row>,
      );

      // Wait a short time to ensure React renders
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const reportSection = document.getElementById("note-card-" + infraction?.id);
      const downloadButton = document.getElementById("note-download-button-" + infraction?.id);
      const editButton = document.getElementById("note-edit-button-" + infraction?.id);

      if (reportSection) {
        if (downloadButton) {
          downloadButton.style.visibility = "hidden";
          downloadButton.style.display = "none";
        }
        if (editButton) {
          editButton.style.visibility = "hidden";
          editButton.style.display = "none";
        }

        const images = reportSection.getElementsByTagName("img");
        for (let i = 0; i < images.length; i++) {
          images[i].crossOrigin = "anonymous";
        }

        const canvas = await html2canvas(reportSection, { useCORS: true });
        canvases.push(canvas);

        if (downloadButton) {
          downloadButton.style.visibility = "visible";
          downloadButton.style.display = "inline-block";
        }
        if (editButton) {
          editButton.style.visibility = "visible";
          editButton.style.display = "inline-block";
        }

        // Clean up
        root.unmount();
        document.body.removeChild(hiddenContainer);
      } else {
        console.error("Report section not found");
      }
    }

    setCanvas(canvases);
  };

  const onClickViewReport = async () => {
    setIsLoading(true);
    let allInspections: any = [];
    await fetchLastBedInspection()
      .then((res: any) => {
        if (res[0]?.data?.last_bed_check?.length) {
          allInspections = [...allInspections, ...res[0]?.data?.last_bed_check];
          allInspections = allInspections.map((all: any) => {
            if (all?.created_at) {
              const utcDate = new Date(all?.created_at + " UTC");
              const localTime = utcDate.toLocaleString(undefined, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });
              return { ...all, inspection_at: localTime };
            } else {
              return all;
            }
          });
        } else {
          filterInspection(filterRoom?.id);
          setShowInspectionReport(true);
          setIsLoading(false);
        }
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        setIsLoading(false);
      });
    setLastInspections(allInspections);
  };

  useEffect(() => {
    if (lastInspections.length) {
      filterInspection(filterRoom?.id);
      setShowInspectionReport(true);
      setIsLoading(false);
    }
  }, [lastInspections]);

  const onClickDone = async () => {
    setShowInspectionReport(false);
    setInfractions([]);
    if (filterRoom === "0") {
      if (viewAll) {
        setFilteredBeds(beds);
      } else {
        const occupiedBeds = beds.filter((b: any) => b.occupant_id);
        setFilteredBeds(occupiedBeds);
      }
    }
  };

  const handleOpen = (note: string) => {
    setSelectedNote(note);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const onClickDownload = async () => {
    setIsDownloading(true);
    // Map filteredInspections to format for InfractionView/pdf generation
    const formatted = filteredInspections.map((i: any) => ({
      id: i.id,
      fields: {
        date: i.created_at.split(" ")[0],
        type: "Bed",
        description: i.note || "",
      },
      firstname: i.created_firstname,
      lastname: i.created_lastname,
      subject: {
        name: i.room_name + ": " + i.bed_name + " - " + i.firstname + " " + (i.lastname ?? "") || "",
      },
    }));
    await downloadReport(formatted);
    setIsDownloading(false);
  };

  return showInspectionReport && !isLoading ? (
    <>
      <PageTitle title="Last Inspection Report" type="section" backButtonText="Back to Bed Inspection" onBackClick={() => onClickDone()} />
      <Row className="pb-5 mt-2 align-items-center">
        <Col xs={6}>
          <RoomFilterSelect rooms={rooms} filterRoom={filterRoom} onUpdate={(e: any) => filterByRoomReport(e)} />
        </Col>
        <Col xs="auto">
          {isDownloading ? (
            <Button className="app-tertiary-color button button-smaller d-flex justify-content-center align-items-center">
              <Spinner className="mt-1" animation="border" style={{ width: "20px", height: "20px" }} />
            </Button>
          ) : (
            <>
              <IconEmbeddedButton
                onClickButton={onClickDownload}
                disabled={!(filteredInspections?.length > 0)}
                icon={faDownload}
                tooltipText="Download Report"
                color={"tertiary"}
              />
            </>
          )}
        </Col>
        <Col xs={12}>
          {filteredInspections?.length > 0 ? (
            <>
              {filterRoom !== "0" && (
                <p className="app-header-text mt-1 mb-1 p-2">
                  {filterRoom?.inspection_at ? "Last Checked " + filterRoom?.inspection_at : "No Occupancy Check Completed"}{" "}
                  {filterRoom?.inspection_firstname && filterRoom?.inspection_lastname
                    ? "By " + filterRoom?.inspection_firstname + " " + (filterRoom?.inspection_lastname ?? "")
                    : ""}{" "}
                </p>
              )}
              <Table striped className="mt-2">
                <thead className="">
                  <tr>
                    {filterRoom === "0" && (
                      <>
                        <th>Date</th>
                        <th>Room Name</th>
                      </>
                    )}
                    <th>Bed Name</th>
                    <th>Occupant Name</th>
                    <th>
                      {"Infractions "}
                      <FontAwesomeIcon
                        data-tooltip-id="infraction-tooltip"
                        data-tooltip-content={"Infractions from this occupant recorded in the past 30 days"}
                        icon={InfoCircleOutline}
                        className="app-primary-color-inverse opacity-75"
                      />
                      <Tooltip id="infraction-tooltip" place="top" style={{ padding: "5px 10px", zIndex: "1100" }} />
                    </th>
                    {filterRoom === "0" && <th>Submitted By</th>}
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections?.map((i: any) => (
                    <tr key={i.id}>
                      {filterRoom === "0" && (
                        <>
                          <td style={{ width: "12rem" }}>{i.inspection_at}</td>
                          <td>{i.room_name}</td>
                        </>
                      )}
                      <td>{i.bed_name}</td>
                      <td>{i.firstname + " " + (i.lastname ?? "")}</td>
                      <td>{i.rolling_infractions}</td>
                      {filterRoom === "0" ? (
                        <>
                          <td>{i.created_firstname + " " + (i.created_lastname ?? "")}</td>
                          <td>
                            <FontAwesomeIcon
                              icon={faFileText}
                              onClick={() => handleOpen(i.note)}
                              style={{
                                cursor: "pointer",
                                color: "#5a7082",
                              }}
                            />
                          </td>
                        </>
                      ) : (
                        <td>{i.note}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : (
            <p className="mt-3">No Failed Inspections Reported</p>
          )}
        </Col>
      </Row>
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>{selectedNote}</Modal.Body>
        <Modal.Footer>
          <Button className="app-primary-color me-2 button rounded" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  ) : (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg.split("\n").map((line: any, index: any) => (
              <span key={index}>
                {line}
                <br />
              </span>
            ))}
          </p>
          <>
            <PageTitle
              title="Bed Inspection"
              type="section"
              buttonColor="secondary"
              buttonTitle="Last Inspection Report"
              onButtonClick={() => onClickViewReport()}
            />
            <Row className={showMenu ? "mt-3 sticky-top card-note-menu-open-sticky" : "mt-3 sticky-top card-note-sticky"}>
              <Col xs={12} lg={10}>
                <div style={{ backgroundColor: "#fff" }}>
                  <Card className="bg-light">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={6}>
                        <RoomFilterSelect
                          rooms={rooms}
                          filterRoom={filterRoom}
                          onUpdate={(e: any) => filterByRoom(e)}
                          disabled={infractions.length > 0 ? true : false}
                        />
                      </Col>
                      <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <InputGroup className="d-flex justify-content-end align-items-center">
                          <Button
                            className={
                              viewOccupied ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"
                            }
                            onClick={() => onClickViewOccupied()}
                          >
                            Occupied
                          </Button>
                          <Button
                            className={viewAll ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"}
                            onClick={() => onClickViewAll()}
                          >
                            All
                          </Button>
                        </InputGroup>
                      </Col>
                    </Row>
                  </Card>
                </div>
                {filteredBeds.length > 0 && (
                  <Card className="border-0 rounded-0">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={12} className="d-flex justify-content-start align-items-center">
                        {infractions.length === 0 ? (
                          <Stack direction="horizontal" gap={3}>
                            <Button className="app-secondary-color button" onClick={() => onClickSubmit()} disabled={isUpdating}>
                              Submit with No Infractions
                            </Button>
                            <div className="vr" />
                            <Button className="app-primary-color button" onClick={() => onClickCancel()} disabled={true}>
                              Cancel
                            </Button>
                            {filterRoom !== "0" && (
                              <p className="app-header-text m-0 p-0">
                                {filterRoom?.inspection_at ? "Last Checked " + filterRoom?.inspection_at : "No Occupancy Check Completed"}{" "}
                                {filterRoom?.inspection_firstname && filterRoom?.inspection_lastname
                                  ? "By " + filterRoom?.inspection_firstname + " " + (filterRoom?.inspection_lastname ?? "")
                                  : ""}{" "}
                              </p>
                            )}
                          </Stack>
                        ) : (
                          <Stack direction="horizontal" gap={3}>
                            <Button className="app-secondary-color button" onClick={() => onClickSubmit()}>
                              Submit with Infractions
                            </Button>
                            <div className="vr" />
                            <Button className="app-primary-color button" onClick={() => onClickCancel()} disabled={false}>
                              Cancel
                            </Button>
                            <FormCheck
                              label="Download Infractions"
                              value={download}
                              onUpdate={(e: any) => setDownload(!download)}
                              disabled={submitted}
                            />
                          </Stack>
                        )}
                      </Col>
                    </Row>
                  </Card>
                )}
              </Col>
            </Row>
            <Row className="pb-3 mt-0">
              <Col xs={12} lg={10} className="pb-3">
                {filteredBeds.length === 0 ? (
                  <Row className="mt-3">
                    <p className="mt-2">No Beds Created</p>
                  </Row>
                ) : (
                  filteredBeds?.map((item: any, index: number) => (
                    <Row key={index}>
                      <Col xs={12}>
                        <Row className="mb-1"></Row>
                        <BedInspectionCard
                          item={item}
                          infractions={infractions}
                          setInfractions={setInfractions}
                          submitted={submitted}
                          setOrgToSave={setOrgToSave}
                        />
                      </Col>
                    </Row>
                  ))
                )}
              </Col>
            </Row>
          </>
        </>
      )}
    </>
  );
};

export default BedInspection;
